import React, { useEffect, useState } from "react";
import { useI18n } from "../context/I18nContext";
import { api } from "../context/AuthContext";
import { money, moneyExplicit } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { ShareNetwork, FilePdf, FileXls } from "@phosphor-icons/react";
import { toast } from "sonner";

export const ReferralPanel = ({ clients }) => {
  const { t } = useI18n();
  const [cfg, setCfg] = useState({ level2_amount: 100, level3_amount: 200, level4_amount: 400, currency: "USD", active: true, welcome_ticket: true, welcome_discount_pct: 10, welcome_discount_type: "pct", welcome_discount_amount: 0, tier_discount_enabled: false, tier1_min: 1, tier1_pct: 5, tier2_min: 3, tier2_pct: 10, tier3_min: 6, tier3_pct: 15, ranking_prize: "" });
  const [tree, setTree] = useState({ nodes: [], tree_id: 1, active_tree_id: 1 });
  const [report, setReport] = useState([]);
  const [saving, setSaving] = useState(false);
  const [placeUser, setPlaceUser] = useState("");
  const [sponsor, setSponsor] = useState("none");
  const [trees, setTrees] = useState([]);
  const [viewTree, setViewTree] = useState(null);
  const [activeNodes, setActiveNodes] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [hall, setHall] = useState([]);

  const load = (tid) => {
    api.get("/admin/referral/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/admin/referral/monthly-ranking").then((r) => setRanking(r.data.ranking || [])).catch(() => {});
    api.get("/admin/referral/hall-of-fame").then((r) => setHall(r.data || [])).catch(() => {});
    api.get("/admin/referral/trees").then((r) => setTrees(r.data)).catch(() => {});
    const q = tid ? `?tree_id=${tid}` : "";
    api.get(`/admin/referral/tree${q}`).then((r) => {
      setTree(r.data);
      // Sponsors always come from the ACTIVE placement tree, even when viewing history
      if (r.data.tree_id === r.data.active_tree_id) {
        setActiveNodes(r.data.nodes || []);
      } else {
        api.get(`/admin/referral/tree?tree_id=${r.data.active_tree_id}`).then((ar) => setActiveNodes(ar.data.nodes || [])).catch(() => {});
      }
    }).catch(() => {});
    api.get(`/admin/referral/report${q}`).then((r) => setReport(r.data)).catch(() => {});
  };
  // Determine the active tree first so the initial selector, tree, sponsors and report share one context
  useEffect(() => {
    api.get("/admin/referral/config").then((r) => {
      setCfg(r.data);
      setViewTree(r.data.active_tree_id || 1);
    }).catch(() => setViewTree(1));
  }, []); // eslint-disable-line
  useEffect(() => { if (viewTree != null) load(viewTree); }, [viewTree]); // eslint-disable-line

  const isViewingActive = viewTree == null || viewTree === (tree.active_tree_id || cfg.active_tree_id);

  const saveCfg = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/referral/config", {
        level2_amount: Number(cfg.level2_amount), level3_amount: Number(cfg.level3_amount),
        level4_amount: Number(cfg.level4_amount), currency: cfg.currency, active: cfg.active, welcome_ticket: cfg.welcome_ticket, welcome_discount_pct: Number(cfg.welcome_discount_pct) || 0, welcome_discount_type: cfg.welcome_discount_type || "pct", welcome_discount_amount: Number(cfg.welcome_discount_amount) || 0, tier_discount_enabled: !!cfg.tier_discount_enabled, tier1_min: Number(cfg.tier1_min) || 1, tier1_pct: Number(cfg.tier1_pct) || 0, tier2_min: Number(cfg.tier2_min) || 1, tier2_pct: Number(cfg.tier2_pct) || 0, tier3_min: Number(cfg.tier3_min) || 1, tier3_pct: Number(cfg.tier3_pct) || 0, ranking_prize: cfg.ranking_prize || "",
      });
      setCfg(data); toast.success(t("ref_saved"));
    } catch (e) { toast.error("Error"); } finally { setSaving(false); }
  };

  const place = async () => {
    if (!placeUser) return;
    try {
      await api.post("/admin/referral/place", { user_id: placeUser, sponsor_id: sponsor === "none" ? null : sponsor });
      toast.success("OK"); setPlaceUser(""); setSponsor("none"); load(viewTree);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const downloadReport = async (fmt) => {
    try {
      const tid = viewTree || tree.active_tree_id || 1;
      const ext = fmt === "excel" ? "xlsx" : "pdf";
      const res = await api.get(`/admin/referral/report/${fmt}?tree_id=${tid}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.data.type || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url; a.download = `reporte_referidos_arbol${tid}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Error"); }
  };

  const childrenMap = {};
  const nodes = tree.nodes || [];
  nodes.forEach((n) => { if (n.parent_id && n.side) childrenMap[`${n.parent_id}:${n.side}`] = n; });
  const rootNode = nodes.find((n) => n.depth === 1) || null;

  const levels = [[rootNode]];
  for (let d = 2; d <= 4; d++) {
    const prev = levels[d - 2];
    const row = [];
    prev.forEach((n) => {
      row.push(n ? (childrenMap[`${n.user_id}:L`] || null) : null);
      row.push(n ? (childrenMap[`${n.user_id}:R`] || null) : null);
    });
    levels.push(row);
  }

  const cur = cfg.currency;
  const perDiv = { level2_amount: 2, level3_amount: 4, level4_amount: 8 };

  return (
    <div className="space-y-6" data-testid="referral-panel">
      <div className="flex items-center gap-2">
        <ShareNetwork size={22} weight="duotone" className="text-emerald-700" />
        <h3 className="font-display font-bold text-emerald-900 text-xl">{t("referral_title")}</h3>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 space-y-4" data-testid="referral-config">
          <h4 className="font-display font-bold text-emerald-900">{t("referral_config")}</h4>
          {[["level2_amount", "lvl2_amount"], ["level3_amount", "lvl3_amount"], ["level4_amount", "lvl4_amount"]].map((pair) => {
            const k = pair[0];
            const label = pair[1];
            return (
              <div key={k}>
                <Label className="text-emerald-900 font-semibold">{t(label)}</Label>
                <Input data-testid={"ref-" + k} type="number" step="0.01" className="mt-1.5 h-11 font-mono-num max-w-xs" value={cfg[k]} onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} />
                <p className="text-xs text-slate-400 mt-1">{money((Number(cfg[k]) || 0) / perDiv[k], cur)} {t("per_person")} ({perDiv[k]})</p>
              </div>
            );
          })}
          <div className="flex items-center gap-6 pt-1">
            <div>
              <Label className="text-emerald-900 font-semibold">{t("ref_currency")}</Label>
              <Select value={cfg.currency} onValueChange={(v) => setCfg({ ...cfg, currency: v })}>
                <SelectTrigger data-testid="ref-currency" className="mt-1.5 h-11 w-40"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="USD">US$ (USD)</SelectItem><SelectItem value="DOP">RD$ (DOP)</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Switch data-testid="ref-active" checked={cfg.active} onCheckedChange={(v) => setCfg({ ...cfg, active: v })} />
              <span className="text-sm text-slate-600">{t("ref_active")}</span>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Switch data-testid="ref-welcome-ticket" checked={cfg.welcome_ticket !== false} onCheckedChange={(v) => setCfg({ ...cfg, welcome_ticket: v })} />
              <span className="text-sm text-slate-600">Regalar 1 boleto de sorteo a socios que se registren con enlace de referido</span>
            </div>
          </div>
          <div>
            <Label className="text-emerald-900 font-semibold">Descuento de bienvenida</Label>
            <div className="flex flex-wrap items-end gap-3 mt-1.5">
              <Select value={cfg.welcome_discount_type || "pct"} onValueChange={(v) => setCfg({ ...cfg, welcome_discount_type: v })}>
                <SelectTrigger data-testid="ref-welcome-discount-type" className="h-11 w-44"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="pct">Porcentaje (%)</SelectItem><SelectItem value="fixed">Monto fijo</SelectItem></SelectContent>
              </Select>
              {(cfg.welcome_discount_type || "pct") === "fixed" ? (
                <Input data-testid="ref-welcome-discount-amount" type="number" step="1" min="0" className="h-11 font-mono-num w-40" value={cfg.welcome_discount_amount ?? 0} onChange={(e) => setCfg({ ...cfg, welcome_discount_amount: e.target.value })} />
              ) : (
                <Input data-testid="ref-welcome-discount" type="number" step="1" min="0" max="100" className="h-11 font-mono-num w-40" value={cfg.welcome_discount_pct ?? 0} onChange={(e) => setCfg({ ...cfg, welcome_discount_pct: e.target.value })} />
              )}
            </div>
            <p className="text-xs text-slate-400 mt-1">Descuento en la primera compra de la tienda para socios que se registran con referido (0 = desactivado). El monto fijo se aplica en la moneda de la compra.</p>
          </div>
          <div className="border-t border-gray-100 pt-4" data-testid="tier-discount-config">
            <div className="flex items-center gap-2">
              <Switch data-testid="ref-tier-enabled" checked={!!cfg.tier_discount_enabled} onCheckedChange={(v) => setCfg({ ...cfg, tier_discount_enabled: v })} />
              <Label className="text-emerald-900 font-semibold">Descuento por niveles (según referidos activos)</Label>
            </div>
            <p className="text-xs text-slate-400 mt-1 mb-3">Descuento continuo en la tienda según cuántos referidos tenga el socio. No se suma al de bienvenida: se aplica el mayor.</p>
            <div className="grid grid-cols-2 gap-3 max-w-md">
              {[1, 2, 3].map((n) => (
                <React.Fragment key={n}>
                  <div>
                    <Label className="text-xs text-slate-500">Nivel {n}: desde (referidos)</Label>
                    <Input data-testid={`ref-tier${n}-min`} type="number" min="1" className="mt-1 h-10 font-mono-num" value={cfg[`tier${n}_min`] ?? 1} onChange={(e) => setCfg({ ...cfg, [`tier${n}_min`]: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Descuento (%)</Label>
                    <Input data-testid={`ref-tier${n}-pct`} type="number" min="0" max="100" className="mt-1 h-10 font-mono-num" value={cfg[`tier${n}_pct`] ?? 0} onChange={(e) => setCfg({ ...cfg, [`tier${n}_pct`]: e.target.value })} />
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="border-t border-gray-100 pt-4">
            <Label className="text-emerald-900 font-semibold">🏆 Premio del ranking del mes</Label>
            <Input data-testid="ref-ranking-prize" className="mt-1.5 h-11 max-w-md" placeholder='Ej: "RD$5,000 + una canasta para el #1"' value={cfg.ranking_prize ?? ""} onChange={(e) => setCfg({ ...cfg, ranking_prize: e.target.value })} />
            <p className="text-xs text-slate-400 mt-1">Se muestra destacado arriba del Top invitadores (admin y socios).</p>
          </div>
          <Button data-testid="ref-save-btn" onClick={saveCfg} disabled={saving} className="bg-emerald-800 hover:bg-emerald-900">{saving ? t("loading") : t("update_rates")}</Button>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 space-y-4" data-testid="referral-place">
            <h4 className="font-display font-bold text-emerald-900">{t("place_client")}</h4>
            {!isViewingActive && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid="ref-place-history-note">
                {t("completed_tree")} — {t("place")}: {t("active_tree")}
              </p>
            )}
            <div>
              <Label className="text-emerald-900 font-semibold">{t("member")}</Label>
              <Select value={placeUser} onValueChange={setPlaceUser} disabled={!isViewingActive}>
                <SelectTrigger data-testid="ref-place-user" className="mt-1.5 h-11"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{(clients || []).map((c) => <SelectItem key={c.user.id} value={c.user.id}>{c.user.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-emerald-900 font-semibold">{t("select_sponsor")}</Label>
              <Select value={sponsor} onValueChange={setSponsor} disabled={!isViewingActive}>
                <SelectTrigger data-testid="ref-place-sponsor" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {activeNodes.map((n) => <SelectItem key={n.user_id} value={n.user_id}>{n.name} (N{n.depth})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="ref-place-btn" onClick={place} disabled={!placeUser || !isViewingActive} className="bg-emerald-800 hover:bg-emerald-900">{t("place")}</Button>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="referral-report">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h4 className="font-display font-bold text-emerald-900">{t("ref_report")}</h4>
              <div className="flex gap-2">
                <Button data-testid="ref-report-pdf" onClick={() => downloadReport("pdf")} size="sm" variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1.5 h-8"><FilePdf size={14} weight="bold" /> PDF</Button>
                <Button data-testid="ref-report-excel" onClick={() => downloadReport("excel")} size="sm" variant="outline" className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 gap-1.5 h-8"><FileXls size={14} weight="bold" /> Excel</Button>
              </div>
            </div>
            {report.length === 0 ? <p className="text-slate-400 text-sm">—</p> : (
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {report.map((r) => (
                  <div key={r.user_id} className="flex justify-between text-sm border-b border-gray-50 pb-1.5" data-testid="ref-report-row">
                    <span className="text-emerald-900">{r.name}</span>
                    <span className="font-mono-num font-semibold text-emerald-700">{Object.keys(r.totals || {}).map((c) => moneyExplicit(r.totals[c], c)).join(" · ") || "—"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="referral-monthly-ranking">
        <h4 className="font-display font-bold text-emerald-900 mb-4">🏆 Top invitadores del mes</h4>
        {cfg.ranking_prize ? <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-2.5 text-sm font-semibold text-amber-800" data-testid="admin-ranking-prize">🎁 Premio del mes: {cfg.ranking_prize}</div> : null}
        {ranking.length === 0 ? <p className="text-slate-400 text-sm" data-testid="ranking-empty">Aún no hay referidos este mes.</p> : (
          <div className="space-y-2">
            {ranking.map((r, i) => (
              <div key={r.user_id} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2.5" data-testid={`ranking-row-${i}`}>
                <span className="flex items-center gap-2.5 font-semibold text-emerald-900">
                  <span className="text-lg w-6 text-center">{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : <span className="text-xs text-slate-400">{i + 1}</span>}</span>
                  {r.name}
                </span>
                <span className="font-mono-num font-bold text-emerald-700">{r.count} referido(s)</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="referral-hall-of-fame">
        <h4 className="font-display font-bold text-emerald-900 mb-4">🏛️ Salón de la Fama</h4>
        {hall.length === 0 ? <p className="text-slate-400 text-sm" data-testid="admin-hall-empty">Aún no hay ganadores de meses anteriores.</p> : (
          <div className="space-y-2">
            {hall.map((h, i) => {
              const names = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
              const [y, mm] = (h.month || "").split("-");
              const lbl = mm ? `${names[Number(mm) - 1]} ${y}` : h.month;
              return (
                <div key={i} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2.5" data-testid={`admin-hall-row-${i}`}>
                  <span className="flex items-center gap-2 font-semibold text-emerald-900"><span className="text-lg">🏆</span> {h.name}</span>
                  <span className="text-xs text-slate-500 capitalize">{lbl} · {h.count} ref.</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid="referral-tree">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h4 className="font-display font-bold text-emerald-900">{t("ref_tree")}</h4>
          <Select value={String(viewTree || tree.active_tree_id || 1)} onValueChange={(v) => setViewTree(Number(v))}>
            <SelectTrigger data-testid="ref-tree-select" className="h-9 w-48 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {trees.map((tr) => (
                <SelectItem key={tr.tree_id} value={String(tr.tree_id)}>
                  {(tr.active ? t("active_tree") : (tr.completed ? t("completed_tree") : t("active_tree")))} #{tr.tree_id} · {tr.count}/15{tr.active ? " ●" : (tr.completed ? " ✓" : "")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto pb-2 space-y-3">
          {levels.map((row, di) => (
            <div key={di} className="flex items-center justify-center gap-3 min-w-max">
              <span className="text-[10px] font-semibold text-slate-400 w-8 shrink-0">N{di + 1}</span>
              {row.map((n, ni) => (
                n
                  ? <div key={ni} className={"px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap shadow-sm " + (n.role === "admin" ? "bg-emerald-800 text-white" : "bg-emerald-100 text-emerald-900")} data-testid={"tree-node-" + n.user_id}>
                      {n.name}{n.side ? <span className="opacity-60"> · {n.side}</span> : null}
                    </div>
                  : <div key={ni} className="px-3 py-2 rounded-lg text-[10px] text-slate-300 border border-dashed border-slate-200" data-testid={"tree-empty-" + di + "-" + ni}>{t("empty_slot")}</div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
